import {
  BOARD_CELLS,
  BOARD_SIZE,
  cellIndex,
  inBounds,
  indexOfX,
  indexOfY,
  indexOfZ,
} from './board';
import { GameError, MAX_OBSERVATIONS, otherPlayer, selectableStoneTypes } from './game';
import { LINES, WIN_LENGTH, linesThroughCell } from './lines';
import { BLACK_PROBABILITY, TARGET_COLOR } from './stones';
import type { GameState, Player, Rng, Stone, StoneColor, StoneType } from './types';
import { checkWin } from './win';

/** CPU の難易度 */
export type CpuDifficulty = 'beginner' | 'normal' | 'advanced' | 'master';

/** 難易度の一覧（UI 用） */
export const CPU_DIFFICULTIES: readonly CpuDifficulty[] = [
  'beginner',
  'normal',
  'advanced',
  'master',
];

/** 難易度の日本語ラベル */
export const CPU_DIFFICULTY_LABEL: Record<CpuDifficulty, string> = {
  beginner: '初級',
  normal: '普通',
  advanced: '上級',
  master: '最強',
};

/** CPU の配置決定の結果 */
export interface CpuPlacement {
  type: StoneType;
  x: number;
  y: number;
  z: number;
}

/** モンテカルロ観測シミュレーションの結果（勝 / 負 / 無の結果の分布） */
export interface ObservationEstimate {
  pWin: number;
  pLoss: number;
  pNone: number;
}

interface CpuConfig {
  /** 自分の目標色のライン潜在値の重み（0 = ライン評価しない: 初級） */
  ownWeight: number;
  /** 相手の目標色のライン潜在値の重み（防御） */
  oppWeight: number;
  /** 盤面中央への偏りの重み（同点の調整用） */
  centerWeight: number;
  /** 先行探索: 展開する自分の最上位候補数（0 = 探索しない） */
  topN: number;
  /** 先行探索: 相手の最善応手として見る手数 */
  oppN: number;
  /** 観測判断のモンテカルロ試行数（0 = 解析のみ） */
  observeTrials: number;
  /** モンテカルロを発動するラインの最小石数（99 = 発動しない） */
  mcMinStones: number;
}

const CPU_CONFIG: Record<CpuDifficulty, CpuConfig> = {
  beginner: {
    ownWeight: 0,
    oppWeight: 0,
    centerWeight: 1,
    topN: 0,
    oppN: 0,
    observeTrials: 0,
    mcMinStones: 99,
  },
  normal: {
    ownWeight: 1,
    oppWeight: 0.5,
    centerWeight: 0.1,
    topN: 0,
    oppN: 0,
    observeTrials: 0,
    mcMinStones: 99,
  },
  advanced: {
    ownWeight: 1,
    oppWeight: 0.85,
    centerWeight: 0.1,
    topN: 0,
    oppN: 0,
    observeTrials: 60,
    mcMinStones: 4,
  },
  master: {
    ownWeight: 1,
    oppWeight: 0.9,
    centerWeight: 0.1,
    topN: 4,
    oppN: 3,
    observeTrials: 100,
    mcMinStones: 4,
  },
};

/** ラインが完成（5 石）した際の値スケール（勝利確率スケール） */
const WIN_WEIGHT = 1000;
/** 未完成ラインの潜在乗数（ライン上の石数 k=0..4 で索引） */
const LINE_POTENTIAL: readonly number[] = [0, 1, 8, 40, 120];
/** 相手の 4 連を即座に塞ぐ配置のボーナス重み */
const BLOCK_WEIGHT = 600;

/** 石の種類が指定色になる確率 */
function colorProbability(type: StoneType, color: StoneColor): number {
  return color === 'black' ? BLACK_PROBABILITY[type] : 1 - BLACK_PROBABILITY[type];
}

/**
 * ラインを「指定色の 5 目が成立しうる度合い」として評価する。
 * - 5 マスすべてに石がある場合: 各石の色の確率積 × WIN_WEIGHT（厳密な確率スケール）
 * - それ以外: 置かれた石の確率積 × 石数ごとの乗数（部分ヒューリスティック）
 *
 * スケールが整合しているため、「4 連を埋める」delta が大きく正になり、
 * 「相手の 4 連に相手の色の低確率石を置く」delta が負になる。
 */
function lineValue(
  cells: readonly number[],
  board: readonly (Stone | null)[],
  color: StoneColor,
  extra: { index: number; type: StoneType } | null,
): number {
  let product = 1;
  let stones = 0;
  for (let i = 0; i < WIN_LENGTH; i++) {
    const idx = cells[i];
    if (extra !== null && idx === extra.index) {
      product *= colorProbability(extra.type, color);
      stones++;
      continue;
    }
    const stone = board[idx];
    if (stone === null) continue;
    product *= colorProbability(stone.type, color);
    stones++;
  }
  return stones === WIN_LENGTH ? WIN_WEIGHT * product : product * LINE_POTENTIAL[stones];
}

/**
 * 配置の候補マス列を作る。
 * 評価量を抑制するため、既存の石の付近（チェビシェフ距離 2 以内）の空きマスに限定する。
 * 盤面が空のときは全マスを使う（1 手目は中央付近に自然に集まる）。
 */
function candidateCells(board: readonly (Stone | null)[], moveCount: number): number[] {
  if (moveCount === 0) {
    const all: number[] = new Array(BOARD_CELLS);
    for (let i = 0; i < BOARD_CELLS; i++) all[i] = i;
    return all;
  }
  const near = new Uint8Array(BOARD_CELLS);
  for (let i = 0; i < BOARD_CELLS; i++) {
    if (board[i] === null) continue;
    const x = indexOfX(i);
    const y = indexOfY(i);
    const z = indexOfZ(i);
    for (let dz = -2; dz <= 2; dz++) {
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          const nz = z + dz;
          if (!inBounds(nx, ny, nz)) continue;
          const idx = cellIndex(nx, ny, nz);
          if (board[idx] === null) near[idx] = 1;
        }
      }
    }
  }
  const cells: number[] = [];
  for (let i = 0; i < BOARD_CELLS; i++) {
    if (board[i] === null && near[i] === 1) cells.push(i);
  }
  return cells;
}

/** 「盤面中心に近い度合い」のスコア（0〜9）。同点調整に使う */
function centerScore(x: number, y: number, z: number): number {
  const c = (BOARD_SIZE - 1) / 2;
  return BOARD_SIZE - 1 - (Math.abs(x - c) + Math.abs(y - c) + Math.abs(z - c));
}

/**
 * 候補マスを「相手の 4 連の空き 5 番目」が塞げているか評価する。
 * 4 つの既存石が相手の色になりうるラインを、この石がどの程度阻害するかで加点する。
 * （ライン潜在値の delta だけでは相手の 4 連の脅威が過小評価されるため）
 */
function blockBonus(
  cells: readonly number[],
  board: readonly (Stone | null)[],
  cell: number,
  type: StoneType,
  opp: StoneColor,
): number {
  let product = 1;
  let stones = 0;
  for (let i = 0; i < WIN_LENGTH; i++) {
    const idx = cells[i];
    if (idx === cell) continue;
    const stone = board[idx];
    if (stone === null) return 0; // 空きが複数: 即 4 連ではない
    product *= colorProbability(stone.type, opp);
    stones++;
  }
  if (stones !== 4) return 0;
  const reduction = product * (1 - colorProbability(type, opp));
  return BLOCK_WEIGHT * reduction;
}

interface RankedMove {
  cell: number;
  type: StoneType;
  score: number;
}

/**
 * 候補（マス + 石の種類）を CPU の視点で採点する。
 * 候補マスを通過する全ラインについて、
 * 「この石を置いた場合」の自玉・敵玉のライン潜在値の delta と中央バイアスの和。
 */
function candidateScore(
  board: readonly (Stone | null)[],
  cell: number,
  type: StoneType,
  own: StoneColor,
  opp: StoneColor,
  cfg: CpuConfig,
): number {
  const extra = { index: cell, type };
  let delta = 0;
  const ids = linesThroughCell(cell);
  for (let li = 0; li < ids.length; li++) {
    const cells = LINES[ids[li]];
    delta += cfg.ownWeight * (lineValue(cells, board, own, extra) - lineValue(cells, board, own, null));
    delta += cfg.oppWeight * (lineValue(cells, board, opp, extra) - lineValue(cells, board, opp, null));
    if (cfg.ownWeight > 0) delta += blockBonus(cells, board, cell, type, opp);
  }
  delta += cfg.centerWeight * centerScore(indexOfX(cell), indexOfY(cell), indexOfZ(cell));
  return delta;
}

/** 盤面に石を置いて新しい配列を返す（元の配列を変更しない） */
function withStone(
  board: readonly (Stone | null)[],
  cell: number,
  type: StoneType,
  owner: Player,
): (Stone | null)[] {
  const next = board.slice();
  next[cell] = { type, owner, observedColor: null };
  return next;
}

/** 盤面全体を指定色の視点で評価する（全ラインの潜在値の和） */
function boardValue(board: readonly (Stone | null)[], color: StoneColor): number {
  let total = 0;
  for (let li = 0; li < LINES.length; li++) {
    total += lineValue(LINES[li], board, color, null);
  }
  return total;
}

/** 相手の候補手を生成して採点し、上位 n 手だけ返す */
function opponentResponses(
  board: readonly (Stone | null)[],
  state: GameState,
  cfg: CpuConfig,
  ownColor: StoneColor,
): RankedMove[] {
  const oppPlayer = otherPlayer(state.currentPlayer);
  const oppColor = TARGET_COLOR[oppPlayer];
  const types = selectableStoneTypes(state, oppPlayer);
  const cells = candidateCells(board, state.moveCount + 1);
  const scored: RankedMove[] = [];
  for (const cell of cells) {
    for (const type of types) {
      scored.push({
        cell,
        type,
        // 相手の視点: 相手の目標色が「自玉」、CPU の目標色が「敵玉」
        score: candidateScore(board, cell, type, oppColor, ownColor, cfg),
      });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, cfg.oppN);
}

/**
 * 限定先行探索: 最上位の自分の候補それぞれについて、
 * 相手が最善応手を打つと仮定し、「最悪値」が最も高い手を選ぶ。
 * 分岐数は topN × oppN で限定し、計算量を制御する。
 */
function lookaheadBest(
  top: RankedMove[],
  state: GameState,
  cfg: CpuConfig,
  ownColor: StoneColor,
): RankedMove {
  const oppPlayer = otherPlayer(state.currentPlayer);
  const oppColor = TARGET_COLOR[oppPlayer];
  let best = top[0];
  let bestScore = -Infinity;
  for (const move of top) {
    const board2 = withStone(state.board, move.cell, move.type, state.currentPlayer);
    const responses = opponentResponses(board2, state, cfg, ownColor);
    if (responses.length === 0) {
      if (move.score > bestScore) {
        bestScore = move.score;
        best = move;
      }
      continue;
    }
    let worst = Infinity;
    for (const resp of responses) {
      const board3 = withStone(board2, resp.cell, resp.type, oppPlayer);
      const value = boardValue(board3, ownColor) - cfg.oppWeight * boardValue(board3, oppColor);
      if (value < worst) worst = value;
    }
    if (worst > bestScore) {
      bestScore = worst;
      best = move;
    }
  }
  return best;
}

/** 初級: 中央・隣接バイアス付きの小規模候補プールからランダム選択（ライン評価はしない） */
function beginnerPlacement(
  board: readonly (Stone | null)[],
  types: readonly StoneType[],
  rng: Rng,
): CpuPlacement {
  const empty: number[] = [];
  for (let i = 0; i < BOARD_CELLS; i++) {
    if (board[i] === null) empty.push(i);
  }
  if (empty.length === 0) throw new GameError('空きマスがありません。');
  const scored: Array<{ cell: number; score: number }> = empty.map((cell) => {
    const x = indexOfX(cell);
    const y = indexOfY(cell);
    const z = indexOfZ(cell);
    let near = 0;
    for (let dz = -2; dz <= 2; dz++) {
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          const nz = z + dz;
          if (!inBounds(nx, ny, nz)) continue;
          if (board[cellIndex(nx, ny, nz)] !== null) near++;
        }
      }
    }
    return { cell, score: centerScore(x, y, z) + near * 2 + rng() * 3 };
  });
  scored.sort((a, b) => b.score - a.score);
  const pool = scored.slice(0, Math.min(16, scored.length));
  const pick = pool[Math.floor(rng() * pool.length)];
  const type = types[Math.floor(rng() * types.length)];
  return {
    type,
    x: indexOfX(pick.cell),
    y: indexOfY(pick.cell),
    z: indexOfZ(pick.cell),
  };
}

/**
 * CPU の配置決定（純粋関数: 状態を変更しない）。
 * 公開情報（石の種類・所有者・プレイヤー状態）のみを使い、observedColor を読まない。
 * 全難易度とも合法な石の種類と空きマスしか選択しない。
 *
 * - 初級: 中央・隣接バイアス付きのランダム
 * - 普通: 自玉・敵玉のライン潜在値で全候補を採点し最良を選ぶ
 * - 上級: 普通 + 強い防御重み + 相手の 4 連の即時ブロックボーナス
 * - 最強: 上級 + 限定先行探索（topN × oppN）
 */
export function decidePlacement(
  state: GameState,
  difficulty: CpuDifficulty,
  rng: Rng,
): CpuPlacement {
  const player = state.currentPlayer;
  const types = selectableStoneTypes(state, player);
  if (types.length === 0) throw new GameError('選択できる石がありません。');
  const cfg = CPU_CONFIG[difficulty];
  const board = state.board;

  if (cfg.ownWeight === 0) {
    return beginnerPlacement(board, types, rng);
  }

  const own = TARGET_COLOR[player];
  const opp = TARGET_COLOR[otherPlayer(player)];
  const cells = candidateCells(board, state.moveCount);
  const candidates: RankedMove[] = [];
  for (const cell of cells) {
    for (const type of types) {
      candidates.push({
        cell,
        type,
        score: candidateScore(board, cell, type, own, opp, cfg) + rng() * 0.01,
      });
    }
  }
  if (candidates.length === 0) throw new GameError('空きマスがありません。');
  candidates.sort((a, b) => b.score - a.score);
  // 先行探索する場合は上位 topN 手を展開し、しない場合は最上位 1 手を選ぶ
  const best =
    cfg.topN > 0
      ? lookaheadBest(candidates.slice(0, Math.min(cfg.topN, candidates.length)), state, cfg, own)
      : candidates[0];
  return {
    type: best.type,
    x: indexOfX(best.cell),
    y: indexOfY(best.cell),
    z: indexOfZ(best.cell),
  };
}

/**
 * 「今観測したらどうなるか」の勝敗分布をモンテカルロで推定する。
 * 読み取り専用: 実際の盤面状態を変更せず、観測権も消費しない。
 * シミュレーションでは各石の色を独立に抽選し、結果を分類する:
 * - 両色が 5 目 → 観測者が勝利
 * - 観測者の目標色のみ 5 目 → 観測者が勝利
 * - 相手の目標色のみ 5 目 → 相手の勝利
 * - どちらでもない → 勝者なし
 * 模擬の色は実際の観測に使わない（実際の観測は Game.observe が新しい乱数で独立抽選する）。
 */
export function estimateObservation(
  board: readonly (Stone | null)[],
  observer: Player,
  trials: number,
  rng: Rng,
): ObservationEstimate {
  let wins = 0;
  let losses = 0;
  let none = 0;
  const colors: (StoneColor | null)[] = new Array(BOARD_CELLS).fill(null);
  for (let t = 0; t < trials; t++) {
    for (let i = 0; i < BOARD_CELLS; i++) {
      const stone = board[i];
      colors[i] = stone === null ? null : (rng() < BLACK_PROBABILITY[stone.type] ? 'black' : 'white');
    }
    const result = checkWin(colors);
    if (result.black && result.white) {
      wins++;
    } else if (result.black) {
      if (observer === 'first') wins++;
      else losses++;
    } else if (result.white) {
      if (observer === 'second') wins++;
      else losses++;
    } else {
      none++;
    }
  }
  return { pWin: wins / trials, pLoss: losses / trials, pNone: none / trials };
}

/** 全ライン中の、指定色の最大ライン潜在値（脅威度の代わり） */
function bestLineValue(board: readonly (Stone | null)[], color: StoneColor): number {
  let best = 0;
  for (let li = 0; li < LINES.length; li++) {
    const v = lineValue(LINES[li], board, color, null);
    if (v > best) best = v;
  }
  return best;
}

/** 完成済みライン（5 石すべて配置済み）で指定色の 5 目が成立する最大確率 */
function bestCompleteLineProb(board: readonly (Stone | null)[], color: StoneColor): number {
  let best = 0;
  for (let li = 0; li < LINES.length; li++) {
    const cells = LINES[li];
    let product = 1;
    let complete = true;
    for (let i = 0; i < WIN_LENGTH; i++) {
      const stone = board[cells[i]];
      if (stone === null) {
        complete = false;
        break;
      }
      product *= colorProbability(stone.type, color);
    }
    if (complete && product > best) best = product;
  }
  return best;
}

/** いずれかのライン上の配置済み石数の最大値（limit で打ち切り） */
function maxStonesOnLine(board: readonly (Stone | null)[], limit: number): number {
  for (let li = 0; li < LINES.length; li++) {
    const cells = LINES[li];
    let stones = 0;
    for (let i = 0; i < WIN_LENGTH; i++) {
      if (board[cells[i]] !== null) stones++;
      if (stones >= limit) return limit;
    }
  }
  return 0;
}

/**
 * CPU の観測判断（純粋関数: 状態を変更しない）。
 * 「盤面にある石の種類確率分布」から判断し、observedColor を読まない。
 * - 初級: 自分の強い 4 連以上があるとき、または観測権が残り 1 の終盤で観測
 * - 普通: 完成ラインの解析確率と、観測権を温存する価値を比較
 * - 上級 / 最強: 盤面に 4 連が出たらモンテカルロで勝敗分布を推定し、
 *   「今観測する」期待値と「温存する」価値（観測権のオプション価値）を比較
 */
export function decideObservation(
  state: GameState,
  difficulty: CpuDifficulty,
  rng: Rng,
): boolean {
  const player = state.currentPlayer;
  const ps = state.players[player];
  if (ps.observationsUsed >= MAX_OBSERVATIONS) return false;
  const cfg = CPU_CONFIG[difficulty];
  const board = state.board;
  const own = TARGET_COLOR[player];
  const opp = TARGET_COLOR[otherPlayer(player)];

  if (difficulty === 'beginner') {
    if (bestLineValue(board, own) >= 12) return true;
    return MAX_OBSERVATIONS - ps.observationsUsed === 1 && state.moveCount >= 40;
  }

  const useMc =
    cfg.observeTrials > 0 && maxStonesOnLine(board, cfg.mcMinStones) >= cfg.mcMinStones;
  let pWin: number;
  let pLoss: number;
  let pNone: number;
  if (useMc) {
    const est = estimateObservation(board, player, cfg.observeTrials, rng);
    pWin = est.pWin;
    pLoss = est.pLoss;
    pNone = est.pNone;
  } else {
    // 解析推定: 完成ラインの最大確率を使う（保守的）
    pWin = bestCompleteLineProb(board, own);
    pLoss = bestCompleteLineProb(board, opp);
    pNone = Math.max(0, 1 - pWin - pLoss);
  }

  const rightsLeft = MAX_OBSERVATIONS - ps.observationsUsed;
  const oppRightsLeft = MAX_OBSERVATIONS - state.players[otherPlayer(player)].observationsUsed;
  // 今観測する期待値: 勝率 − 負率 + 「勝者なし」の情報価値（わずかに評価）
  const utilObserve = pWin - pLoss + 0.1 * pNone;
  // 温存する価値: オプション価値 + 相手も観測権を持つリスク
  const utilSkip = 0.1 + (oppRightsLeft > 0 ? 0.1 : 0) - (rightsLeft > 1 ? 0.05 : 0);
  return utilObserve >= utilSkip;
}
